/**
 * @file LegalPdfFileBlock.tsx
 * @description Блок файла PDF-документа (phase19.16, SB139; uiux-legal-docs
 * §3, §7): значок и имя файла, размер, чей это файл (новый в черновике или
 * файл редакции N), «Загрузить / Заменить файл», «Открыть», «Вернуть файл
 * редакции»; загрузка с процентом и «Отменить»; ошибки файла — под блоком.
 *
 * Предпросмотр на первом этапе — значок и имя (миниатюра первой страницы —
 * позже, SB139). Под блоком родитель кладёт историю файлов по редакциям.
 */

import { formatLegalFileSize, LEGAL_PDF_MAX_BYTES, type LegalFileInfoDto } from "@chaiberi/shared";
import { FileText, Upload } from "lucide-react";
import { useId, useRef, type ReactNode } from "react";

import { Button } from "../ui/button";
import { NoticeStrip } from "../ui/NoticeStrip";
import type { LegalFileUpload } from "../../hooks/legal/useLegalFileUpload";
import { formatLegalDate } from "../../lib/legal/legal-doc-view";

interface Props {
  /** Файл, который уйдёт в редакцию; null — файла ещё нет. */
  file: LegalFileInfoDto | null;
  live: { number: number; publishedAt: string } | null;
  upload: Pick<LegalFileUpload, "progress" | "error" | "cancel">;
  onPick: (file: File) => void;
  /** Открыть файл; нет — кнопки «Открыть» нет (новый документ). */
  onOpen?: () => void;
  /** Вернуть файл действующей редакции (убрать новый файл черновика). */
  onRevert?: () => void;
  disabled?: boolean;
  /** История файлов по редакциям. */
  children?: ReactNode;
}

const MAX_MB = Math.round(LEGAL_PDF_MAX_BYTES / (1024 * 1024));

/** Чей файл: новый в черновике или файл редакции. */
function fileOrigin(file: LegalFileInfoDto, live: Props["live"]): string {
  if (file.source === "draft") return live ? "Новый файл — заменит файл на сайте после публикации" : "Уйдёт на сайт при публикации";
  return live ? `Файл редакции ${live.number} от ${formatLegalDate(live.publishedAt)}` : "Файл документа";
}

export function LegalPdfFileBlock({ file, live, upload, onPick, onOpen, onRevert, disabled, children }: Props) {
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const pick = () => input.current?.click();

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-on-surface">Файл</span>
      <input
        ref={input}
        id={inputId}
        type="file"
        accept="application/pdf,.pdf"
        className="sr-only"
        tabIndex={-1}
        aria-label="Выбрать PDF-файл"
        onChange={(e) => {
          const chosen = e.target.files?.[0];
          // Сбрасываем, чтобы тот же файл можно было выбрать повторно после ошибки.
          e.target.value = "";
          if (chosen) onPick(chosen);
        }}
      />
      <div className="flex flex-col gap-3 rounded-xl bg-surface-lowest p-3 [box-shadow:inset_0_0_0_1px_var(--border)]">
        {file ? (
          <div className="flex min-w-0 items-start gap-3">
            <FileText aria-hidden className="mt-0.5 size-8 shrink-0 text-primary" />
            <span className="flex min-w-0 flex-col">
              <span className="type-body break-words text-on-surface">{file.name}</span>
              <span className="type-caption text-on-surface-variant">
                PDF · {formatLegalFileSize(file.sizeBytes)} · {fileOrigin(file, live)}
              </span>
            </span>
          </div>
        ) : (
          <p className="type-body text-on-surface-variant">Файла нет. Загрузите PDF до {MAX_MB} МБ без пароля.</p>
        )}

        {upload.progress ? (
          <div className="flex flex-col gap-1" role="status" aria-live="polite">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="type-body break-words text-on-surface">
                Загружаем {upload.progress.name} — {upload.progress.percent}%
              </span>
              <Button variant="ghost" className="min-h-12" onClick={upload.cancel}>
                Отменить
              </Button>
            </div>
            <progress className="h-1.5 w-full accent-[var(--primary)]" max={100} value={upload.progress.percent} aria-label="Загрузка файла" />
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="min-h-12" onClick={pick} disabled={disabled}>
              <Upload aria-hidden className="size-4" />
              {file ? "Заменить файл" : "Загрузить файл"}
            </Button>
            {file && onOpen ? (
              <Button variant="ghost" className="min-h-12" onClick={onOpen} disabled={disabled}>
                Открыть
              </Button>
            ) : null}
            {file?.source === "draft" && live && onRevert ? (
              <Button variant="ghost" className="min-h-12" onClick={onRevert} disabled={disabled}>
                Вернуть файл редакции {live.number}
              </Button>
            ) : null}
          </div>
        )}
      </div>
      {upload.error ? <NoticeStrip tone="error">{upload.error}</NoticeStrip> : null}
      {children}
    </div>
  );
}
